import React, { PureComponent } from 'react';
import { IUserWorkoutSessionSummary } from 'fitbeat.models';
import { translate } from 'react-i18next';
import {
  Text,
  View,
  Platform,
  StyleSheet,
  Dimensions,
} from 'react-native';
import { calcTotalMinutes, getActivityTypeText, getTotalMinutesCeil } from 'fitbeat.gym-core';
import Theme from '../../theme';
import { defaultScale } from '../../utils/scaling';
import { getBodyImage } from '../../utils/bodyGroupImage';
import { getActivityTypeIcon } from '../../utils/activityIconMapping';
import DurationIcon from '../../assets/icons/workout-history-header-icons/duration.svg';
import ImportOriginIcon from '../../assets/icons/workout-history-header-icons/import-origin.svg';
import EffortPointsIcon from '../../assets/icons/workout-history-header-icons/effort-points.svg';
import KiloCalIcon from '../../assets/images/kilo-calories.svg';
import EffortPercentageIcon from '../../assets/icons/workout-history-header-icons/effort-percentage.svg';
import WorkoutTypeIcon from '../../assets/icons/workout-history-header-icons/workout-type.svg';
import BodyGroupsIcon from '../../assets/icons/workout-history-header-icons/target-body-group.svg';
import ExclamationIcon from '../../assets/images/exclamation.svg';
import { getConnectedAppIcon } from '../../ConnectedApps/ui';
import { WorkoutHistoryRow } from './WorkoutHistoryRow';

const { width } = Dimensions.get('window');
const isMobile = width <= 550;
const activityIconSize = Platform.OS === 'web' ? 60 : 60;

interface IWorkoutHistoryModalBodyProps {
  t: (key: string) => string;
  gender?: 'male' | 'female';
  workoutHistory: IUserWorkoutSessionSummary;
  userProfileService: any; // TODO: Define proper user profile service type
}

interface IWorkoutHistoryModalBodyState {
  partnerProfile: any; // TODO: Define proper partner profile type
  isLoading: boolean;
}

class WorkoutHistoryModalBody extends PureComponent<IWorkoutHistoryModalBodyProps, IWorkoutHistoryModalBodyState> {
  constructor(props: IWorkoutHistoryModalBodyProps) {
    super(props);

    this.state = {
      partnerProfile: {},
      isLoading: false,
    };
  }

  public componentDidMount() {
    this.fetchPartnerProfile().catch(console.error);
  }

  public componentDidUpdate(prevProps) {
    if (this.props.workoutHistory
            && (prevProps.workoutHistory.pairedSessionUserProfileId
                !== this.props.workoutHistory.pairedSessionUserProfileId)) {
      this.fetchPartnerProfile().catch(console.error);
    }
  }

  public fetchPartnerProfile = async (): Promise<void> => {
    const { workoutHistory, userProfileService } = this.props;
    if (workoutHistory && workoutHistory.pairedSessionUserProfileId) {
      await this.setState({ isLoading: true });
      const partnerProfile = await userProfileService.getUserProfileDetails(
        workoutHistory.pairedSessionUserProfileId,
      );
      await this.setState({ partnerProfile, isLoading: false });
    }
  };

  public getIconSize = (): number => {
    if (Platform.OS === 'web') {
      return isMobile ? 20 : 35;
    }
    return 20;
  };

  public getOriginIconSize = (): number => {
    if (Platform.OS === 'web') {
      return isMobile ? 25 : 34;
    }
    return 25;
  };

  public render(): React.ReactNode {
    const {
      t,
      gender = 'female',
      workoutHistory,
    } = this.props;
    const {
      importOrigin,
      settings: {
        cardioOrResistance,
        threeBodyGroups,
        activityType,
      },
      performanceMetrics,
      overlappedByWorkoutId,
    } = workoutHistory;

    const metrics = performanceMetrics || {
      load: '-',
      calories: '-',
      averageHeartRate: '-',
    };

    const iconSize = this.getIconSize();
    const iconColour = Theme.GREY;
    const totalMinutes = calcTotalMinutes(workoutHistory);
    const workoutMinutes = getTotalMinutesCeil(workoutHistory.workoutType, totalMinutes);
    const formattedWorkoutDuration = WorkoutHistoryRow.formatDuration(workoutMinutes);
    const importOriginIcon = importOrigin?.origin ?? 'Fitillion';
    const isOverlapped = overlappedByWorkoutId !== undefined;
    return (
      <View>
        {isOverlapped && (
        <View style={styles.warningContainer}>
          <View style={styles.warningIconContainer}>
            <ExclamationIcon color={Theme.GREY_DARK} />
          </View>
          <View style={styles.warningTextContainer}>
            <Text style={styles.warningheading}>{t('activityExcludedFromProgress')}</Text>
            <Text style={styles.warningdescription}>{t('overlapWarningDescription1')}</Text>
            <Text style={styles.warningdescription} />
            <Text style={styles.warningdescription}>{t('overlapWarningDescription2')}</Text>
          </View>
        </View>
        )}
        <View style={styles.row}>
          <View style={[styles.settingContainer, styles.sectionBorderRight]}>
            <View style={styles.workoutTypeIconContainer}>
              {getActivityTypeIcon(activityType, undefined, activityIconSize)}
            </View>
            <Text style={styles.workoutType}>{`${getActivityTypeText(activityType)}`}</Text>
            <View style={styles.labelContainer}>
              <WorkoutTypeIcon
                color={iconColour}
                width={defaultScale(iconSize)}
                height={defaultScale(iconSize)}
              />
              <Text style={styles.label}>{t('workoutType')}</Text>
            </View>
          </View>

          <View style={[styles.settingContainer, styles.sectionBorderRight]}>
            <View style={styles.block}>
              <View style={[styles.workoutSettingsContainer, styles.bodyGroupContainer]}>
                {getBodyImage(
                  threeBodyGroups,
                  gender,
                  cardioOrResistance,
                  defaultScale(100),
                  defaultScale(100),
                )}
              </View>
            </View>
            <View style={styles.labelContainer}>
              <BodyGroupsIcon
                color={iconColour}
                width={defaultScale(iconSize)}
                height={defaultScale(iconSize)}
              />
              <Text style={styles.label}>{t('bodyGroups')}</Text>
            </View>
          </View>

          <View style={styles.settingContainer}>
            <View style={[styles.subSettingContainer, styles.sectionBorderBottom]}>
              <View style={styles.durationContainer}>
                <Text style={styles.duration}>{`${formattedWorkoutDuration}`}</Text>
              </View>
              <View style={styles.labelContainer}>
                <DurationIcon
                  color={iconColour}
                  width={defaultScale(iconSize)}
                  height={defaultScale(iconSize)}
                />
                <Text style={styles.label}>{t('duration')}</Text>
              </View>
            </View>
            <View style={[styles.subSettingContainer]}>

              <View style={[styles.block, styles.flex]}>
                <View style={[styles.workoutSettingsContainer, styles.flex]}>
                  {getConnectedAppIcon(
                    importOriginIcon,
                    defaultScale(this.getOriginIconSize()),
                  )}
                </View>
              </View>
              <View style={styles.labelContainer}>
                <ImportOriginIcon
                  color={iconColour}
                  width={defaultScale(iconSize - 5)}
                  height={defaultScale(iconSize - 5)}
                />
                <Text style={styles.label}>{t('importOrigin')}</Text>
              </View>
            </View>
          </View>
        </View>

        <View style={[styles.row, styles.noBorderBottom]}>
          <View style={[styles.settingContainer, styles.sectionBorderRight]}>
            <View style={styles.block}>
              <View style={styles.metricContainer}>
                <Text style={styles.metric}>{metrics.load}</Text>
              </View>
            </View>
            <View style={styles.labelContainer}>
              <EffortPointsIcon
                color={iconColour}
                width={defaultScale(iconSize)}
                height={defaultScale(iconSize)}
              />
              <Text style={styles.label}>{t('effortPoints')}</Text>
            </View>
          </View>

          <View style={[styles.settingContainer, styles.sectionBorderRight]}>
            <View style={styles.block}>
              <View style={styles.metricContainer}>
                <Text style={styles.metric}>{metrics.calories}</Text>
              </View>
            </View>
            <View style={styles.labelContainer}>
              <KiloCalIcon
                color={iconColour}
                width={defaultScale(iconSize)}
                height={defaultScale(iconSize)}
              />
              <Text style={styles.label}>{t('kcals')}</Text>
            </View>
          </View>

          <View style={styles.settingContainer}>
            <View style={styles.block}>
              <View style={styles.metricContainer}>
                <Text style={styles.metric}>{`${metrics.averageHeartRate}`}</Text>
              </View>
            </View>
            <View style={styles.labelContainer}>
              <EffortPercentageIcon
                color={iconColour}
                width={defaultScale(iconSize)}
                height={defaultScale(iconSize)}
              />
              <Text style={styles.label}>{t('Heart Rate')}</Text>
            </View>
          </View>
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
    flexBasis: 0,
  },
  row: {
    width: '100%',
    paddingVertical: defaultScale(20),
    flexDirection: 'row',
    borderBottomWidth: defaultScale(1.5),
    borderBottomColor: Theme.GREY_LIGHT,
  },
  noBorderBottom: {
    borderBottomWidth: 0,
  },
  settingContainer: {
    flex: 1,
    flexBasis: 0,
    paddingHorizontal: defaultScale(3),
  },
  sectionBorderBottom: {
    borderBottomWidth: defaultScale(1.5),
    borderBottomColor: Theme.GREY_LIGHT,
    paddingBottom: defaultScale(5),
  },
  sectionBorderRight: {
    borderRightWidth: defaultScale(1.5),
    borderRightColor: Theme.GREY_LIGHT,
  },
  durationContainer: {
    flex: 1,
    justifyContent: 'center',
    ...Platform.select({
      web: {
        padding: 20,
      },
    }),
  },
  bodyGroupContainer: {
    ...Platform.select({
      web: {
        height: '100%',
        width: '100%',
      },
    }),
  },
  subSettingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  originIcon: {
    ...Theme.textStyle.heading,
  },
  warningContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: defaultScale(10),
    margin: defaultScale(15),
    backgroundColor: Theme.GREY_LIGHT,
    borderRadius: defaultScale(8),
  },
  warningIconContainer: {
    paddingTop: defaultScale(2),
    paddingRight: defaultScale(10),
  },
  warningTextContainer: {
    flex: 1,
  },
  warningheading: {
    ...Theme.textStyle.bodyCopyBold,
    color: Theme.GREY_DARK,
    marginBottom: defaultScale(5),
    ...Platform.select({
      web: {
        fontSize: 16,
      },
    }),
  },
  warningdescription: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_DARK,
    ...Platform.select({
      web: {
        fontSize: 14,
      },
    }),
  },
  importOrigin: {
    ...Theme.textStyle.bodyCopyBold,
    paddingTop: defaultScale(5),
    textAlign: 'center',
    color: Theme.CHARCOAL,
    ...Platform.select({
      web: {
        fontSize: 20,
      },
    }),
  },
  duration: {
    ...Theme.textStyle.heading,
    fontFamily: Theme.CustomBold,
    color: Theme.CHARCOAL,
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: defaultScale(-5),
  },
  label: {
    ...Theme.textStyle.small,
    paddingLeft: defaultScale(3),
    alignSelf: 'center',
    textAlign: 'center',
    fontFamily: Theme.CustomBold,
    color: Theme.GREY,
    ...Platform.select({
      web: {
        fontSize: 18,
      },
    }),
  },
  block: {
    alignItems: 'center',
    ...Platform.select({
      web: {
        flex: 1,
      },
    }),
  },
  workoutType: {
    ...Theme.textStyle.smallBold,
    paddingBottom: defaultScale(10),
    textAlign: 'center',
  },
  workoutTypeImage: {
    width: defaultScale(20),
    height: defaultScale(20),
    marginTop: defaultScale(5),
    ...Platform.select({
      web: {
        width: 40,
        height: 40,
        marginTop: 20,
      },
    }),
  },
  workoutTypeIconContainer: {
    flex: 1,
    flexDirection: 'row',
    marginTop: defaultScale(15),
    marginRight: defaultScale(10),
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        padding: 20,
      },
    }),
  },
  workoutTypeLabelContainer: {
    ...Platform.select({
      web: {
        flex: 0,
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
      },
    }),
  },
  workoutSettingsContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  metricContainer: {
    paddingBottom: defaultScale(10),
    alignItems: 'center',
    justifyContent: 'center',
  },
  metric: {
    ...Theme.textStyle.heading,
    textAlign: 'center',
    color: Theme.CHARCOAL,
  },
});

export default translate(['workoutHistory'], { wait: true })(WorkoutHistoryModalBody);
